import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@/components/ui/typography';

import { Separator } from '@/components/ui/separator';
import { Button, Card, CardContent } from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MoreVertical, RefreshCw, Trash2 } from 'lucide-react';
import { TeamMember } from '@/types/api';
import DeleteInvitationModal from './DeleteInvitationModal';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/components/ui/toast';
import { AppDispatch, RootState } from '@/store/store';
import {
  resendInvitation,
  resetResendInvitationError,
  resetResendInvitationSuccess,
} from '@/store/invitationsSlice';
import { SvgIcon } from '@/components/ui/SvgIcon';

const InvitedUserCard: React.FC<{
  user: TeamMember;
  tenantId: string;
}> = ({ user, tenantId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { addToast } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [hasInitiatedResend, setHasInitiatedResend] = React.useState(false);
  const { hasPermission } = usePermissions();

  const {
    resendInvitationLoading,
    resendInvitationError,
    resendInvitationSuccess,
  } = useSelector((state: RootState) => state.invitations);

  // Only show dropdown menu if user has write-user or delete-user permissions
  const canResend = hasPermission('write-user');
  const canDelete = hasPermission('delete-user');
  const hasActions = canResend || canDelete;

  // Show success toast when resend is successful
  React.useEffect(() => {
    if (
      hasInitiatedResend &&
      !resendInvitationLoading &&
      !resendInvitationError &&
      resendInvitationSuccess
    ) {
      addToast({
        message: resendInvitationSuccess,
        type: 'success',
        duration: 3000,
      });
      setHasInitiatedResend(false);
      dispatch(resetResendInvitationSuccess());
    }
  }, [
    hasInitiatedResend,
    resendInvitationLoading,
    resendInvitationError,
    resendInvitationSuccess,
    addToast,
    dispatch,
  ]);

  // Show error toast when resend fails
  React.useEffect(() => {
    if (
      hasInitiatedResend &&
      !resendInvitationLoading &&
      resendInvitationError
    ) {
      addToast({
        message: resendInvitationError,
        type: 'error',
        duration: 5000,
      });
      setHasInitiatedResend(false);
    }
  }, [
    hasInitiatedResend,
    resendInvitationLoading,
    resendInvitationError,
    addToast,
  ]);

  const handleResendInvite = () => {
    if (tenantId) {
      dispatch(
        resendInvitation({
          email: user.email,
          roleName: user.role,
          ehrUsername: user.ehrUsername ?? undefined,
          tenantId,
        }),
      );
      setHasInitiatedResend(true);
    }
  };

  const handleDeleteInvite = () => {
    setIsDeleteModalOpen(true);
  };

  // Cleanup effect to reset resend error and success when component unmounts
  React.useEffect(() => {
    return () => {
      if (resendInvitationError) {
        dispatch(resetResendInvitationError());
      }
      if (resendInvitationSuccess) {
        dispatch(resetResendInvitationSuccess());
      }
    };
  }, [dispatch, resendInvitationError, resendInvitationSuccess]);

  return (
    <>
      <Card className="rounded-xl border border-gray-200 p-2 sm:p-3 md:p-4 relative hover:border-[#5536F5]/30 hover:shadow-md">
        <CardContent className="p-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 min-w-0 flex-1">
              <div className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 flex items-center justify-center flex-shrink-0">
                <SvgIcon
                  type="inviteUser"
                  color="primary"
                  className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5"
                />
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Typography
                    variant="h4"
                    weight="normal"
                    className="text-neutral-900 text-xs sm:text-sm md:text-base truncate min-w-0 cursor-help"
                  >
                    {user.firstName} {user.lastName}
                  </Typography>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs sm:text-sm">
                    {user.firstName} {user.lastName}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Only show dropdown menu if user has appropriate permissions */}
            {hasActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 sm:h-8 sm:w-8 p-0 hover:bg-[#5536F5]/5 hover:text-[#5536F5] flex-shrink-0"
                  >
                    <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32 sm:w-40">
                  {canResend && (
                    <DropdownMenuItem
                      onClick={handleResendInvite}
                      disabled={resendInvitationLoading}
                      className="text-xs sm:text-sm cursor-pointer hover:bg-[#5536F5]/5"
                    >
                      <RefreshCw
                        className={`h-3 w-3 sm:h-4 sm:w-4 mr-2 ${
                          resendInvitationLoading ? 'animate-spin' : ''
                        }`}
                      />
                      {resendInvitationLoading ? 'Resending...' : 'Resend'}
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem
                      onClick={handleDeleteInvite}
                      className="text-red-600 focus:text-red-600 text-xs sm:text-sm cursor-pointer hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <Separator className="my-2 sm:my-3 md:my-4 bg-[#5536F5]/10" />

          <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 sm:gap-4 md:gap-10">
              <Typography
                variant="small"
                className="text-neutral-500 w-12 sm:w-16 md:w-20 text-xs sm:text-sm flex-shrink-0"
              >
                Role
              </Typography>
              <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1">
                <SvgIcon
                  type="inviteRole"
                  color="primary"
                  className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Typography
                      variant="small"
                      className="text-neutral-700 truncate text-xs sm:text-sm cursor-help min-w-0"
                    >
                      {user.role}
                    </Typography>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs sm:text-sm">{user.role}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 md:gap-10">
              <Typography
                variant="small"
                className="text-neutral-500 w-12 sm:w-16 md:w-20 text-xs sm:text-sm flex-shrink-0"
              >
                Email
              </Typography>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-1.5 sm:space-x-2 cursor-help min-w-0 flex-1">
                      <SvgIcon
                        type="inviteEmail"
                        color="primary"
                        className="h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-4 md:w-4 flex-shrink-0"
                      />
                      <Typography
                        variant="small"
                        className="text-neutral-700 truncate text-xs sm:text-sm min-w-0"
                      >
                        {user.email}
                      </Typography>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs break-words text-xs sm:text-sm">
                      {user.email}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteInvitationModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        userEmail={user.email}
        userUuid={user.uuid}
        tenantId={tenantId}
      />
    </>
  );
};

export default InvitedUserCard;
